"use strict";
/**
 * Segment Evaluation Module
 *
 * This module handles the evaluation of segment membership.
 * Segments are reusable groups of users defined by conditions.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.evaluateSegmentMembership = evaluateSegmentMembership;
exports.evaluateAllSegments = evaluateAllSegments;
exports.getSegmentReferences = getSegmentReferences;
exports.createEmailDomainSegment = createEmailDomainSegment;
exports.createOrganizationSegment = createOrganizationSegment;
const conditions_1 = require("./conditions");
/**
 * Evaluate whether a user is a member of a segment
 *
 * @param segment - The segment definition
 * @param context - The evaluation context
 * @param resolvedFlags - Already resolved flag values (segments can reference flags)
 * @param otherSegments - Other segment memberships (segments can reference other segments)
 * @returns Segment membership result
 */
function evaluateSegmentMembership(segment, context, resolvedFlags = {}, otherSegments = {}) {
    try {
        const isMember = (0, conditions_1.evaluateCondition)(segment.condition, context, resolvedFlags, otherSegments);
        return {
            segmentId: segment.id,
            isMember,
            reason: isMember ? "condition_matched" : "condition_not_matched",
        };
    }
    catch (error) {
        return {
            segmentId: segment.id,
            isMember: false,
            reason: `error: ${error instanceof Error ? error.message : "unknown error"}`,
        };
    }
}
/**
 * Build a dependency graph for segments
 * Returns a map of segment ID to its segment dependencies
 */
function buildSegmentDependencyGraph(segments) {
    const graph = new Map();
    for (const [segmentId, segment] of Object.entries(segments)) {
        graph.set(segmentId, getSegmentReferences(segment));
    }
    return graph;
}
/**
 * Detect cycles in the segment dependency graph using DFS
 * Returns the first cycle found, or undefined if no cycles exist
 */
function detectSegmentCycle(graph) {
    const visited = new Set();
    const recursionStack = new Set();
    const path = [];
    function dfs(node) {
        visited.add(node);
        recursionStack.add(node);
        path.push(node);
        const dependencies = graph.get(node) ?? [];
        for (const dep of dependencies) {
            if (!visited.has(dep)) {
                const cycle = dfs(dep);
                if (cycle) {
                    return cycle;
                }
            }
            else if (recursionStack.has(dep)) {
                // Found a cycle - extract it from the path
                const cycleStart = path.indexOf(dep);
                return [...path.slice(cycleStart), dep];
            }
        }
        path.pop();
        recursionStack.delete(node);
        return undefined;
    }
    for (const node of graph.keys()) {
        if (!visited.has(node)) {
            const cycle = dfs(node);
            if (cycle) {
                return cycle;
            }
        }
    }
    return undefined;
}
/**
 * Topologically sort segments based on their dependencies
 * Segments with no dependencies come first, then segments that depend on them, etc.
 */
function topologicalSortSegments(segments) {
    const graph = buildSegmentDependencyGraph(segments);
    // Check for cycles first
    const cycle = detectSegmentCycle(graph);
    if (cycle) {
        throw new Error(`Cyclic segment dependency detected: ${cycle.join(" -> ")}`);
    }
    const sorted = [];
    const visited = new Set();
    const visiting = new Set();
    function visit(node) {
        if (visited.has(node)) {
            return;
        }
        if (visiting.has(node)) {
            // This shouldn't happen since we checked for cycles above
            throw new Error(`Cyclic segment dependency detected: ${node}`);
        }
        visiting.add(node);
        const dependencies = graph.get(node) ?? [];
        for (const dep of dependencies) {
            // Only visit if the dependency exists in our segments
            if (graph.has(dep)) {
                visit(dep);
            }
        }
        visiting.delete(node);
        visited.add(node);
        sorted.push(node);
    }
    for (const node of graph.keys()) {
        visit(node);
    }
    return sorted;
}
/**
 * Evaluate all segments for a user
 *
 * Uses topological sorting to handle segment dependencies correctly.
 * Segments are evaluated in dependency order so that when a segment
 * references another segment, the referenced segment is already evaluated.
 *
 * @param segments - Map of segment ID to segment definition
 * @param context - The evaluation context
 * @param resolvedFlags - Already resolved flag values
 * @returns Map of segment ID to membership result
 * @throws Error if circular segment dependencies are detected
 */
function evaluateAllSegments(segments, context, resolvedFlags = {}) {
    const memberships = {};
    // Get topologically sorted segment order (handles dependencies correctly)
    // This will throw if circular dependencies are detected
    const sortedSegments = topologicalSortSegments(segments);
    // Evaluate segments in dependency order
    for (const segmentId of sortedSegments) {
        const segment = segments[segmentId];
        if (segment) {
            memberships[segmentId] = evaluateSegmentMembership(segment, context, resolvedFlags, memberships);
        }
    }
    return memberships;
}
/**
 * Get all segment IDs referenced by a segment
 */
function getSegmentReferences(segment) {
    const references = [];
    collectSegmentReferences(segment.condition, references);
    return references;
}
/**
 * Recursively collect segment references from a condition
 */
function collectSegmentReferences(condition, references) {
    if (!condition || typeof condition !== "object") {
        return;
    }
    const conditionObj = condition;
    if (conditionObj.type === "segment") {
        const segmentId = conditionObj.segmentId;
        if (typeof segmentId === "string") {
            references.push(segmentId);
        }
    }
    if (conditionObj.type === "and" || conditionObj.type === "or") {
        const conditions = conditionObj.conditions;
        if (Array.isArray(conditions)) {
            for (const subCondition of conditions) {
                collectSegmentReferences(subCondition, references);
            }
        }
    }
}
/**
 * Create a simple segment for email domain matching
 */
function createEmailDomainSegment(id, name, domain) {
    return {
        id,
        name,
        description: `Users with email ending in ${domain}`,
        condition: {
            type: "attribute",
            attribute: "user.email",
            operator: "endsWith",
            value: domain,
        },
        inputs: {
            userAttributes: ["user.email"],
            systemAttributes: [],
            segments: [],
            flags: [],
        },
        updatedAt: Date.now(),
    };
}
/**
 * Create a segment for users in specific organizations
 */
function createOrganizationSegment(id, name, organizationUids) {
    return {
        id,
        name,
        description: `Users in organizations: ${organizationUids.join(", ")}`,
        condition: {
            type: "or",
            conditions: organizationUids.map((orgUid) => ({
                type: "attribute",
                attribute: "user.organizationUids",
                operator: "contains",
                value: orgUid,
            })),
        },
        inputs: {
            userAttributes: ["user.organizationUids"],
            systemAttributes: [],
            segments: [],
            flags: [],
        },
        updatedAt: Date.now(),
    };
}
//# sourceMappingURL=segments.js.map